const { fetchJSON } = window.BAO.Utils

class StoreSwitcher extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    /* Force redirect - Start */
    if (window.location.href.indexOf('#geo') > -1) {
      return this.forceRedirect()
    }
    /* Force redirect - End */

    if (this.countryParam) {
      this.setupCountrySwitcher(this.countryParam)
      this.updateShippingLabel(this.countryParam)
      return
    }

    const storedCode = window.localStorage.getItem(this.userLocationName)
    const storedSubfolder = storedCode
      ? this.countries[storedCode.toUpperCase()]?.subfolder
      : null
    const currentSubfolder = this.marketCountry
      ? this.countries[this.marketCountry]?.subfolder
      : null

    if (this.marketCountry && storedSubfolder !== currentSubfolder) {
      // Subfolder changed — user switched markets, sync localStorage to new market
      window.localStorage.setItem(this.userLocationName, this.marketCountry)
      this.setupCountrySwitcher(this.marketCountry)
      this.updateShippingLabel(this.marketCountry)
    } else if (!this.noLocationStored) {
      // Stored country is on the same subfolder — use it (preserves specific country within shared-subfolder markets)
      this.setupCountrySwitcher(storedCode)
      this.updateShippingLabel(storedCode)
    } else if (this.marketCountry) {
      // No stored preference, but on a subfolder URL — save and use it
      window.localStorage.setItem(this.userLocationName, this.marketCountry)
      this.setupCountrySwitcher(this.marketCountry)
      this.updateShippingLabel(this.marketCountry)
    } else {
      fetchJSON('/browsing_context_suggestions.json', {
        method: 'GET',
      }).then((res) => {
        const countryCode = res.detected_values.country.handle

        this.setupCountrySwitcher(countryCode)
        this.updateShippingLabel(countryCode)
      })
    }
  }

  forceRedirect () {
    fetchJSON('/browsing_context_suggestions.json', {
      method: 'GET',
    }).then((res) => {
      const userCountry = res.detected_values.country.handle

      let windowPathname = window.location.pathname

      const subdomainFolder = windowPathname.split('/').filter((item) => {
        return item.indexOf('en-') > -1
      })

      if (subdomainFolder) {
        windowPathname = windowPathname
          .replace(subdomainFolder, '')
          .replace('//', '/')
      }

      const { site, subfolder } = this.countries[userCountry]
      const subfolderSegment = subfolder ? `/${subfolder}` : ''
      window.location.href = `${this.selectedSites[site].url}${subfolderSegment}${windowPathname}`
    })
  }

  setupListeners () {
    super.setupListeners()

    this.onTriggerClick = this.onTriggerClick.bind(this)
    this.onLinkClick = this.onLinkClick.bind(this)
    this.onConfirmClick = this.onConfirmClick.bind(this)

    this.onDrawerTriggerClick = this.onDrawerTriggerClick.bind(this)
    this.onDrawerLinkClick = this.onDrawerLinkClick.bind(this)
    this.onDrawerConfirmClick = this.onDrawerConfirmClick.bind(this)

    if (this.els?.trigger?.exists) {
      this.els.trigger.elements.forEach((triggerEl) => {
        this.listeners.add(triggerEl, 'click', this.onTriggerClick)
      })
    }

    document.addEventListener('click', this.onLinkClick)

    if (this.els?.confirm?.exists) {
      this.els.confirm.elements.forEach((confirmEl) => {
        this.listeners.add(confirmEl, 'click', this.onConfirmClick)
      })
    }

    if (this.externalEls?.trigger?.exists) {
      this.externalEls.trigger.elements.forEach((triggerEl) => {
        this.listeners.add(triggerEl, 'click', this.onDrawerTriggerClick)
      })
    }

    document.addEventListener('click', this.onDrawerLinkClick)

    if (this.externalEls?.confirm?.exists) {
      this.externalEls.confirm.elements.forEach((confirmEl) => {
        this.listeners.add(confirmEl, 'click', this.onDrawerConfirmClick)
      })
    }
  }

  onDrawerConfirmClick () {
    const currentLink = document.querySelector(
      `${this.externalEls?.link?.selector}[aria-current="true"]`,
    )
    if (!currentLink) return

    const code = currentLink.getAttribute('data-country-code')
    if (code) window.localStorage.setItem(this.userLocationName, code)

    const href = currentLink.getAttribute('href')
    window.location.href = href
  }

  onDrawerLinkClick (event) {
    const linkSelector = this.externalEls?.link?.selector
    if (!event.target.closest(linkSelector)) return

    event.preventDefault()

    const allLinks = document.querySelectorAll(linkSelector)
    allLinks.forEach((link) => link.removeAttribute('aria-current'))

    const clickedLink = event.target.closest(linkSelector)
    const label = clickedLink.innerHTML

    clickedLink.setAttribute('aria-current', 'true')

    this.externalEls.drawer.element.setAttribute('aria-hidden', 'true')
    this.externalEls.trigger.element.setAttribute('aria-expanded', 'false')
    this.externalEls['trigger-text'].element.innerHTML = label
    this.externalEls.confirm.element.disabled = false
  }

  onDrawerTriggerClick () {
    const currentTriggerState =
      this.externalEls.trigger.element.getAttribute('aria-expanded') === 'true'
    this.externalEls.trigger.element.setAttribute(
      'aria-expanded',
      !currentTriggerState,
    )

    const currentDrawerState =
      this.externalEls.drawer.element.getAttribute('aria-hidden') === 'true'
    this.externalEls.drawer.element.setAttribute(
      'aria-hidden',
      !currentDrawerState,
    )
  }

  onTriggerClick () {
    const currentTriggerState =
      this.els.trigger.element.getAttribute('aria-expanded') === 'true'
    this.els.trigger.element.setAttribute(
      'aria-expanded',
      !currentTriggerState,
    )

    const currentOptionsState =
      this.els.options.element.getAttribute('aria-hidden') === 'true'
    this.els.options.element.setAttribute('aria-hidden', !currentOptionsState)
  }

  onLinkClick (event) {
    const linkSelector = this.els?.link?.selector
    if (!event.target.closest(linkSelector)) return

    event.preventDefault()

    const allLinks = document.querySelectorAll(linkSelector)
    allLinks.forEach((link) => link.removeAttribute('aria-current'))

    const clickedLink = event.target.closest(linkSelector)
    const label = clickedLink.innerHTML

    clickedLink.setAttribute('aria-current', 'true')

    this.els.options.element.setAttribute('aria-hidden', 'true')
    this.els.trigger.element.setAttribute('aria-expanded', 'false')
    this.els['trigger-text'].element.innerHTML = label
  }

  onConfirmClick (event) {
    event.preventDefault()

    const currentLink = document.querySelector(
      `${this.els?.link?.selector}[aria-current="true"]`,
    )
    if (!currentLink) return

    const code = currentLink.getAttribute('data-country-code')
    if (code) window.localStorage.setItem(this.userLocationName, code)

    const href = currentLink.getAttribute('href')
    window.location.href = href
  }

  updateShippingLabel (countryCode) {
    const upperCountryCode = countryCode.toUpperCase()

    if (this.countries[upperCountryCode] !== undefined) {
      const name = this.countries[upperCountryCode].name

      if (this.els?.['trigger-text']?.exists) {
        this.els['trigger-text'].elements.forEach((countryEl) => {
          countryEl.innerHTML = `${name}`
        })
      }
    }

    if (this.noLocationStored) return

    if (this.countries[upperCountryCode] !== undefined) {
      const name = this.countries[upperCountryCode].name

      if (this.externalEls?.['trigger-text']?.exists) {
        this.externalEls['trigger-text'].elements.forEach(
          (countryExternalEl) => {
            countryExternalEl.innerHTML = `${name}`
          },
        )
      }

      if (this.externalEls?.['drawer-trigger']?.exists) {
        this.externalEls['drawer-trigger'].elements.forEach(
          (countryExternalEl) => {
            countryExternalEl.innerHTML = `${name}`
          },
        )
      }
    }
  }

  setupCountrySwitcher (countryCode) {
    this.createOptions(countryCode)

    this.identifyKlaviyoUser(countryCode)

    const normalizedCountryCode = countryCode.toUpperCase()
    if (this.countries[normalizedCountryCode]?.site === this.dataset.store) {
      window.localStorage.setItem(this.userLocationName, normalizedCountryCode)
    }

    if (!this.noLocationStored) return

    this.setAttribute('aria-hidden', 'false')
    document.body.classList.add('util-SiteOverflowed')
  }

  identifyKlaviyoUser (countryCode) {
    const data = {
      CountryCode: countryCode,
    }

    if (window.theme.klaviyo.email) {
      data.$email = window.theme.klaviyo.email
    }

    if (window.klaviyo) {
      window.klaviyo.identify(data)
    }
  }

  createOptions (countryCode) {
    Object.entries(this.countries).forEach(([key, value]) => {
      const { name, site, subfolder } = value

      const isCurrentSite = key.toLowerCase() === countryCode.toLowerCase()
      this.els.options.element.innerHTML += this.createModalElement(
        key,
        site,
        isCurrentSite,
        name,
        subfolder,
      )

      this.externalEls.drawer.element.innerHTML += this.createDrawerElement(
        key,
        site,
        isCurrentSite,
        name,
        subfolder,
      )
    })
  }

  createModalElement (code, site, isCurrentSite, country, subfolder) {
    const href = this.createCountryAwareHref(code, site, subfolder)

    return `<li class="mod-StoreSwitcher_Item">
      <a
        class="mod-StoreSwitcher_Link fz-12_130"
        href="${href}"
        aria-current="${isCurrentSite ? 'true' : 'false'}"
        data-country-code="${code}"
        data-store-switcher-el="link"
      >
        ${country}
      </a>
    </li>`
  }

  createDrawerElement (code, site, isCurrentSite, country, subfolder) {
    const href = this.createCountryAwareHref(code, site, subfolder)

    return `<li class="drw-StoreSwitcher_Item">
      <a
        class="drw-StoreSwitcher_Link fz-12_130"
        href="${href}"
        aria-current="${isCurrentSite ? 'true' : 'false'}"
        data-country-code="${code}"
        data-store-switcher-external-el="link"
      >
        ${country}
      </a>
    </li>`
  }

  createCountryAwareHref (countryCode, site, subfolder) {
    const subfolderSegment = subfolder ? `/${subfolder}` : ''
    // eslint-disable-next-line compat/compat
    const targetUrl = new URL(
      `${this.selectedSites[site].url}${subfolderSegment}${this.cleanPageUrl}`,
    )

    // Cross-store navigation needs a country
    if (targetUrl.origin !== window.location.origin) {
      targetUrl.searchParams.set('country', countryCode)
    }

    return targetUrl.toString()
  }

  get cleanPageUrl () {
    const pageUrl = this.dataset.pageUrl
    const subfolders = Object.values(this.countries)
      .map((c) => c.subfolder)
      .filter(Boolean)
    for (const subfolder of subfolders) {
      if (pageUrl.startsWith(`/${subfolder}/`)) {
        return pageUrl.slice(`/${subfolder}`.length)
      }
    }
    return pageUrl
  }

  get countryParam () {
    // eslint-disable-next-line compat/compat
    const countryCode = new URLSearchParams(window.location.search).get(
      'country',
    )
    if (!countryCode) return null

    const normalizedCountryCode = countryCode.toUpperCase()
    return this.countries[normalizedCountryCode] ? normalizedCountryCode : null
  }

  get noLocationStored () {
    return (
      window.localStorage.getItem(this.userLocationName) == null ||
      (window.localStorage.getItem(this.userLocationName) === undefined &&
        !window.Shopify.designMode)
    )
  }

  get userLocationName () {
    return 'PP-BAO-localization-2'
  }

  get isPrivateClient () {
    return this.dataset.privateClient === 'true'
  }

  get isStaging () {
    return this.dataset.staging === 'true'
  }

  get isQa () {
    return this.dataset.qa === 'true'
  }

  get selectedSites () {
    if (this.isStaging) {
      return this.stagingSites
    }
    if (this.isQa) {
      return this.qaSites
    }
    return this.isPrivateClient ? this.pcSites : this.defaultSites
  }

  get defaultSites () {
    return {
      INT: {
        url: 'https://www.phoebephilo.com',
      },
      US: {
        url: 'https://phoebe-philo-us.myshopify.com',
      },
    }
  }

  get pcSites () {
    return {
      INT: {
        url: 'https://pc.phoebephilo.com',
      },
      US: {
        url: 'https://pc-phoebe-philo-us.myshopify.com',
      },
    }
  }

  get stagingSites () {
    return {
      INT: {
        url: 'https://pp-staging-site.myshopify.com',
      },
      US: {
        url: 'https://pp-staging-site.myshopify.com',
      },
    }
  }

  get qaSites () {
    return {
      INT: {
        url: 'https://phoebe-philo-qa.myshopify.com',
      },
      US: {
        url: 'https://phoebe-philo-qa.myshopify.com',
      },
    }
  }

  // Returns the active market country if the user is on a non-root market subfolder URL,
  // null for the UK primary market (root) where no explicit selection has been made
  get marketCountry () {
    const code = this.dataset.currentCountry?.toUpperCase()
    if (!code || !this.countries[code]) return null
    return this.countries[code].subfolder ? code : null
  }

  get countries () {
    return window.theme.countries
  }
}

if (!customElements.get('store-switcher')) {
  customElements.define('store-switcher', StoreSwitcher)
}
